import type { ReactNode } from "react";
import { ChevronDown, LogOut } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type UserMenuProps = {
  displayName: string;
  tagline?: string;
  title?: string;
  onSignOut: () => void;
};

type AccountDropdownProps = {
  trigger: ReactNode;
  onSignOut: () => void;
};

function initials(displayName: string): string {
  const parts = displayName
    .trim()
    .split(/\s+/)
    .filter((part) => !/^(dr|mr|mrs|ms)\.?$/i.test(part));
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "";
  return `${first}${last}`.toUpperCase() || "U";
}

function AccountDropdown({ trigger, onSignOut }: AccountDropdownProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuGroup>
          <DropdownMenuItem onSelect={onSignOut}>
            <LogOut data-icon="inline-start" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function UserMenu({ displayName, tagline, title, onSignOut }: UserMenuProps) {
  const accountLabel = `Account menu for ${displayName}`;

  return (
    <>
      <span className="md:hidden">
        <AccountDropdown
          onSignOut={onSignOut}
          trigger={
            <Button variant="ghost" size="icon" aria-label={accountLabel} title={title}>
              <Avatar>
                <AvatarFallback>{initials(displayName)}</AvatarFallback>
              </Avatar>
            </Button>
          }
        />
      </span>

      <span className="hidden md:inline-flex">
        <AccountDropdown
          onSignOut={onSignOut}
          trigger={
            <Button
              variant="ghost"
              size="lg"
              aria-label={accountLabel}
              title={title}
              className="max-w-[55vw]"
            >
              <span className="flex min-w-0 flex-col items-end text-right leading-tight">
                <span className="max-w-full truncate text-sm font-medium">{displayName}</span>
                {tagline ? (
                  <span className="max-w-full truncate text-xs font-normal text-muted-foreground">
                    {tagline}
                  </span>
                ) : null}
              </span>
              <ChevronDown data-icon="inline-end" aria-hidden="true" />
            </Button>
          }
        />
      </span>
    </>
  );
}
